(() => {
  const queueFrame = (element, callback) => {
    if (!element.isConnected) return 0;
    element.pendingFrames ||= new Set();
    const frame = requestAnimationFrame((now) => {
      element.pendingFrames.delete(frame);
      if (element.isConnected) callback(now);
    });
    element.pendingFrames.add(frame);
    return frame;
  };
  const clearFrame = (element, frame) => {
    cancelAnimationFrame(frame);
    element.pendingFrames?.delete(frame);
  };
  const clearFrames = (element) => {
    element.pendingFrames?.forEach((frame) => cancelAnimationFrame(frame));
    element.pendingFrames?.clear();
  };

  if (!customElements.get('dh-home-carousel')) {

  class DhHomeCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-dh-track]');
      this.thumb = this.querySelector('[data-dh-progress-thumb]');
      this.progress = this.querySelector('[data-dh-progress]');
      if (!this.track || this.dataset.ready === 'true') return;

      this.dataset.ready = 'true';
      this.dragging = false;
      this.raf = 0;
      this.loopBreakpoint = Number(this.dataset.loopBreakpoint) || 639;
      this.loopMode = this.dataset.loopMode || 'breakpoint';
      this.loopEnabled = this.shouldLoop();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDragStart = (event) => event.preventDefault();
      this.onProgressPointerDown = this.onProgressPointerDown.bind(this);
      this.onProgressPointerMove = this.onProgressPointerMove.bind(this);
      this.onProgressPointerUp = this.onProgressPointerUp.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('keydown', this.onKeyDown);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.progress?.addEventListener('pointerdown', this.onProgressPointerDown);
      this.progress?.addEventListener('pointermove', this.onProgressPointerMove);
      this.progress?.addEventListener('pointerup', this.onProgressPointerUp);
      this.progress?.addEventListener('pointercancel', this.onProgressPointerUp);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.buildLoop();
      queueFrame(this, () => {
        this.measureLoop();
        this.jumpTo(this.firstRealScroll);
        this.updateProgress();
      });
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('keydown', this.onKeyDown);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.progress?.removeEventListener('pointerdown', this.onProgressPointerDown);
      this.progress?.removeEventListener('pointermove', this.onProgressPointerMove);
      this.progress?.removeEventListener('pointerup', this.onProgressPointerUp);
      this.progress?.removeEventListener('pointercancel', this.onProgressPointerUp);
      window.removeEventListener('resize', this.onResize);
      clearFrames(this);
      this.track.classList.remove('is-dragging', 'is-jumping');
      this.dragging = false;
      this.progressDragging = false;
      this.track.querySelectorAll('[data-dh-clone]').forEach((slide) => slide.remove());
      delete this.dataset.ready;
    }

    onScroll() {
      clearFrame(this, this.raf);
      this.raf = queueFrame(this, () => {
        this.normalizeLoop();
        this.updateProgress();
      });
    }

    shouldLoop() {
      if (this.loopMode === 'always') return true;
      if (this.loopMode === 'none') return false;
      return window.matchMedia(`(max-width: ${this.loopBreakpoint}px)`).matches;
    }

    updateProgress() {
      if (!this.cardStep || !this.realSlides?.length) return;
      const padding = Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      const firstWidth = this.realSlides[0]?.getBoundingClientRect().width || this.cardStep;
      const contentWidth = firstWidth + this.cardStep * Math.max(0, this.realSlides.length - 1);
      const referenceViewport = Math.min(1200, Math.max(1, this.track.clientWidth - padding * 2));
      const thumbRatio = Math.min(1, Math.max(0.2, referenceViewport / Math.max(1, contentWidth)));

      if (this.thumb) this.thumb.style.width = `${thumbRatio * 100}%`;

      let ratio = 0;
      if (this.loopEnabled) {
        const rawPosition = Math.round(((this.track.scrollLeft - this.firstRealScroll) / this.cardStep) * 1000) / 1000;
        const wrappedPosition = ((rawPosition % this.realSlides.length) + this.realSlides.length) % this.realSlides.length;
        ratio = this.realSlides.length < 2 ? 0 : Math.min(1, wrappedPosition / (this.realSlides.length - 1));
      } else {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        ratio = maxScroll > 0 ? Math.min(1, Math.max(0, this.track.scrollLeft / maxScroll)) : 0;
      }

      if (this.thumb && this.progress) {
        const maxThumbTravel = Math.max(0, this.progress.clientWidth - this.thumb.offsetWidth);
        this.thumb.style.transform = `translateX(${ratio * maxThumbTravel}px)`;
      }
      if (this.progress) this.progress.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    }

    buildLoop() {
      this.track.querySelectorAll('[data-dh-clone]').forEach((slide) => slide.remove());
      this.realSlides = [...this.track.querySelectorAll('[data-dh-slide]:not([data-dh-clone])')];
      if (!this.loopEnabled || this.realSlides.length < 2) return;

      const makeClone = (slide) => {
        const clone = slide.cloneNode(true);
        clone.dataset.dhClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('aria-label');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, textarea, select, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
        return clone;
      };
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      this.realSlides.forEach((slide) => before.append(makeClone(slide)));
      this.realSlides.forEach((slide) => after.append(makeClone(slide)));
      this.track.prepend(before);
      this.track.append(after);
    }

    measureLoop() {
      if (!this.realSlides?.length) return;
      this.measuredTrackWidth = this.track.clientWidth;
      const first = this.realSlides[0];
      const second = this.realSlides[1];
      const trackLeft = this.track.getBoundingClientRect().left;
      const contentLeft = (element) => element.getBoundingClientRect().left - trackLeft + this.track.scrollLeft;
      const padding = Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      this.cardStep = second ? contentLeft(second) - contentLeft(first) : first.getBoundingClientRect().width;
      const alignedInset = getComputedStyle(first).scrollSnapAlign === 'center'
        ? (this.track.clientWidth - first.offsetWidth) / 2
        : padding;
      this.firstRealScroll = contentLeft(first) - alignedInset;
      this.lastRealScroll = contentLeft(this.realSlides[this.realSlides.length - 1]) - alignedInset;
      this.alignedInset = alignedInset;
    }

    onResize() {
      if (!this.realSlides?.length) return;
      const loopEnabled = this.shouldLoop();
      if (this.track.clientWidth === this.measuredTrackWidth && loopEnabled === this.loopEnabled) return;
      const activeIndex = this.cardStep
        ? Math.round((this.track.scrollLeft - this.firstRealScroll) / this.cardStep)
        : 0;
      if (loopEnabled !== this.loopEnabled) {
        this.loopEnabled = loopEnabled;
        this.buildLoop();
      }
      this.measureLoop();
      const normalizedIndex = ((activeIndex % this.realSlides.length) + this.realSlides.length) % this.realSlides.length;
      this.jumpTo(this.firstRealScroll + normalizedIndex * this.cardStep);
      this.updateProgress();
    }

    normalizeLoop() {
      if (!this.loopEnabled || !this.cardStep || this.realSlides.length < 2) return 0;
      const period = this.cardStep * this.realSlides.length;
      const rawIndex = Math.round((this.track.scrollLeft - this.firstRealScroll) / this.cardStep);
      const cycle = Math.floor(rawIndex / this.realSlides.length);
      if (cycle === 0) return 0;
      const shift = -cycle * period;
      this.jumpTo(this.track.scrollLeft + shift);
      if (this.dragging) this.startScroll += shift;
      return shift;
    }

    jumpTo(left) {
      if (!Number.isFinite(left)) return;
      clearFrame(this, this.modeResetFrame);
      this.track.classList.add('is-jumping');
      this.track.scrollLeft = left;
      this.modeResetFrame = queueFrame(this, () => this.track.classList.remove('is-jumping'));
    }

    snapToClosest(projectedLeft = this.track.scrollLeft) {
      if (!this.cardStep || !this.realSlides?.length) return;
      const padding = Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      const trackLeft = this.track.getBoundingClientRect().left;
      const contentLeft = (element) => element.getBoundingClientRect().left - trackLeft + this.track.scrollLeft;
      const closest = [...this.track.querySelectorAll('[data-dh-slide]')].reduce((best, slide) => {
        const target = contentLeft(slide) - (this.alignedInset ?? padding);
        const distance = Math.abs(target - projectedLeft);
        return !best || distance < best.distance ? { target, distance } : best;
      }, null);
      if (!closest) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollTo({ left: Math.min(maxScroll, Math.max(0, closest.target)), behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    onPointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('a, button, input, textarea, select')) return;
      this.dragging = true;
      this.startX = event.clientX;
      this.startScroll = this.track.scrollLeft;
      this.lastX = event.clientX;
      this.lastTime = performance.now();
      this.velocity = 0;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      event.preventDefault();
      const now = performance.now();
      this.velocity = (this.lastX - event.clientX) / Math.max(1, now - this.lastTime);
      this.lastX = event.clientX;
      this.lastTime = now;
      this.track.scrollLeft = this.startScroll - (event.clientX - this.startX);
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      const velocity = performance.now() - this.lastTime < 90 ? this.velocity : 0;
      const maximumMomentum = (this.cardStep || 1) * Math.max(1, this.realSlides.length - 1);
      const momentum = Math.max(-maximumMomentum, Math.min(maximumMomentum, velocity * 420));
      this.snapToClosest(this.track.scrollLeft + momentum);
    }

    onKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const distance = this.cardStep || this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: event.key === 'ArrowRight' ? distance : -distance, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    onProgressPointerDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.progressDragging = true;
      this.progress.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
      this.onProgressPointerMove(event);
    }

    onProgressPointerMove(event) {
      if (!this.progressDragging) return;
      const rect = this.progress.getBoundingClientRect();
      const thumbWidth = this.thumb?.offsetWidth || 0;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left - thumbWidth / 2) / Math.max(1, rect.width - thumbWidth)));
      this.track.scrollLeft = this.loopEnabled
        ? this.firstRealScroll + ratio * (this.lastRealScroll - this.firstRealScroll)
        : ratio * (this.track.scrollWidth - this.track.clientWidth);
    }

    onProgressPointerUp(event) {
      if (!this.progressDragging) return;
      this.progressDragging = false;
      this.track.classList.remove('is-dragging');
      if (this.progress.hasPointerCapture(event.pointerId)) this.progress.releasePointerCapture(event.pointerId);
      this.snapToClosest();
    }
  }

  customElements.define('dh-home-carousel', DhHomeCarousel);
  }

  if (!customElements.get('dh-ingredient-carousel')) {
    class DhIngredientCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-dh-ingredient-viewport]');
        this.track = this.querySelector('[data-dh-ingredient-track]');
        this.status = this.querySelector('[data-dh-ingredient-status]');
        if (!this.viewport || !this.track || this.dataset.ready === 'true') return;

        this.realSlides = [...this.track.querySelectorAll('[data-dh-ingredient-slide]:not([data-dh-clone])')];
        if (!this.realSlides.length) return;

        this.dataset.ready = 'true';
        this.slideCount = this.realSlides.length;
        this.interval = Math.max(2000, Number(this.dataset.interval) || 3000);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion && this.slideCount > 1;
        this.dragging = false;
        this.hovered = false;
        this.focused = false;
        this.pointerHeld = false;
        this.raf = 0;
        this.normalizeTimer = 0;
        this.autoplayTimer = 0;
        this.scrollAnimationFrame = 0;
        this.remainingInterval = this.interval;
        this.nextAutoplayAt = 0;
        this.announceNext = false;
        this.inViewport = false;
        this.suppressClickUntil = 0;

        this.onScroll = this.onScroll.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onDragStart = (event) => event.preventDefault();

        if (this.slideCount > 1) this.buildLoop();
        this.slides = [...this.track.querySelectorAll('[data-dh-ingredient-slide]')];

        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        this.viewport.addEventListener('keydown', this.onKeyDown);
        this.viewport.addEventListener('pointerenter', this.onMouseEnter);
        this.viewport.addEventListener('pointerleave', this.onMouseLeave);
        this.viewport.addEventListener('focusin', this.onFocusIn);
        this.viewport.addEventListener('focusout', this.onFocusOut);
        this.viewport.addEventListener('wheel', this.onWheel, { passive: true });
        this.viewport.addEventListener('click', this.onClick);
        this.viewport.addEventListener('dragstart', this.onDragStart);
        window.addEventListener('resize', this.onResize, { passive: true });
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          if (!this.isConnected) return;
          this.inViewport = entry.isIntersecting;
          if (this.inViewport) this.scheduleAutoplay();
          else this.pauseAutoplay();
        }, { threshold: 0.15 });
        this.visibilityObserver.observe(this);

        this.measuredViewportWidth = this.viewport.clientWidth;
        queueFrame(this, () => {
          this.jumpTo(this.slideCount > 1 ? this.slideCount : 0);
          this.updateActive();
          this.scheduleAutoplay();
        });
      }

      disconnectedCallback() {
        if (!this.viewport) return;
        this.viewport.removeEventListener('scroll', this.onScroll);
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerUp);
        this.viewport.removeEventListener('keydown', this.onKeyDown);
        this.viewport.removeEventListener('pointerenter', this.onMouseEnter);
        this.viewport.removeEventListener('pointerleave', this.onMouseLeave);
        this.viewport.removeEventListener('focusin', this.onFocusIn);
        this.viewport.removeEventListener('focusout', this.onFocusOut);
        this.viewport.removeEventListener('wheel', this.onWheel);
        this.viewport.removeEventListener('click', this.onClick);
        this.viewport.removeEventListener('dragstart', this.onDragStart);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        clearFrames(this);
        clearTimeout(this.normalizeTimer);
        clearTimeout(this.autoplayTimer);
        clearTimeout(this.wheelTimer);
        this.visibilityObserver?.disconnect();
        this.viewport.classList.remove('is-dragging', 'is-animating');
        this.trackingPointer = false;
        this.dragging = false;
        this.pointerHeld = false;
        this.inViewport = false;
        this.track.querySelectorAll('[data-dh-clone]').forEach((slide) => slide.remove());
        delete this.dataset.ready;
      }

      get paused() {
        return Boolean(this.hovered || this.focused || this.pointerHeld);
      }

      makeClone(slide) {
        const clone = slide.cloneNode(true);
        clone.dataset.dhClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('role');
        clone.removeAttribute('aria-label');
        clone.removeAttribute('aria-current');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone
          .querySelectorAll('a, button, input, textarea, select, [tabindex]')
          .forEach((element) => element.setAttribute('tabindex', '-1'));
        return clone;
      }

      buildLoop() {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.realSlides.forEach((slide) => before.append(this.makeClone(slide)));
        this.realSlides.forEach((slide) => after.append(this.makeClone(slide)));
        this.track.prepend(before);
        this.track.append(after);
      }

      physicalIndex(scrollLeft = this.viewport.scrollLeft) {
        const viewportCenter = scrollLeft + this.viewport.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      logicalIndex(physicalIndex) {
        if (this.slideCount < 1) return 0;
        const offset = this.slideCount > 1 ? physicalIndex - this.slideCount : physicalIndex;
        return ((offset % this.slideCount) + this.slideCount) % this.slideCount;
      }

      scrollPosition(index) {
        const slide = this.slides[index];
        if (!slide) return this.viewport.scrollLeft;
        return slide.offsetLeft - (this.viewport.clientWidth - slide.offsetWidth) / 2;
      }

      goTo(index, behavior = 'smooth') {
        const target = Math.max(0, Math.min(this.slides.length - 1, index));
        const targetLeft = this.scrollPosition(target);
        clearFrame(this, this.scrollAnimationFrame);
        clearFrame(this, this.modeResetFrame);

        if (behavior !== 'smooth' || this.reducedMotion) {
          this.viewport.classList.add('is-animating');
          this.viewport.scrollTo({ left: targetLeft, behavior: 'auto' });
          this.modeResetFrame = queueFrame(this, () => {
            this.viewport.classList.remove('is-animating');
            this.updateActive(true);
          });
          return;
        }

        const startLeft = this.viewport.scrollLeft;
        const distance = targetLeft - startLeft;
        if (Math.abs(distance) < 1) {
          this.viewport.classList.remove('is-animating');
          this.updateActive(true);
          return;
        }

        const duration = Math.min(650, 250 + Math.max(0, Math.abs(distance) - this.viewport.clientWidth / 3) * 0.18);
        const startTime = performance.now();
        this.viewport.classList.add('is-animating');

        const animate = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.viewport.scrollLeft = startLeft + distance * eased;
          if (progress < 1) {
            this.scrollAnimationFrame = queueFrame(this, animate);
          } else {
            this.viewport.classList.remove('is-animating');
            this.viewport.scrollLeft = targetLeft;
            this.updateActive(true);
          }
        };

        this.scrollAnimationFrame = queueFrame(this, animate);
      }

      jumpTo(index) {
        this.goTo(index, 'auto');
      }

      updateActive(announceSettled = false) {
        if (!this.slides?.length) return;
        const activeIndex = this.physicalIndex();
        this.activeLogicalIndex = this.logicalIndex(activeIndex);

        this.slides.forEach((slide, index) => {
          slide.classList.toggle('is-active', index === activeIndex);
          slide.classList.toggle('is-before', index < activeIndex);
          slide.classList.toggle('is-after', index > activeIndex);
          if (index === activeIndex && !slide.hasAttribute('data-dh-clone')) {
            slide.setAttribute('aria-current', 'true');
          } else {
            slide.removeAttribute('aria-current');
          }
        });

        // The first scroll frame normally still centers the previous image.
        // Announce only when the requested movement has actually settled.
        if (announceSettled && this.announceNext && this.status) {
          const logical = this.logicalIndex(activeIndex);
          const source = this.realSlides[logical];
          const label = source?.getAttribute('aria-label') || `Image ${logical + 1}`;
          this.status.textContent = `${logical + 1} of ${this.slideCount}: ${label}`;
          this.announceNext = false;
        }
      }

      normalizeLoop() {
        if (this.dragging || this.slideCount < 2) return;
        const activeIndex = this.physicalIndex();
        if (activeIndex < this.slideCount) {
          const period = this.slides[this.slideCount].offsetLeft - this.slides[0].offsetLeft;
          this.shiftLoop(period);
        } else if (activeIndex >= this.slideCount * 2) {
          const period = this.slides[this.slideCount].offsetLeft - this.slides[0].offsetLeft;
          this.shiftLoop(-period);
        }
        this.updateActive(true);
      }

      shiftLoop(distance) {
        clearFrame(this, this.modeResetFrame);
        this.viewport.classList.add('is-animating');
        this.viewport.scrollLeft += distance;
        this.modeResetFrame = queueFrame(this, () => this.viewport.classList.remove('is-animating'));
      }

      pauseAutoplay() {
        if (this.nextAutoplayAt) {
          this.remainingInterval = Math.max(50, this.nextAutoplayAt - performance.now());
        }
        clearTimeout(this.autoplayTimer);
        this.nextAutoplayAt = 0;
      }

      scheduleAutoplay(useRemaining = false) {
        clearTimeout(this.autoplayTimer);
        if (!this.isConnected || !this.autoplay || this.paused || this.dragging || !this.inViewport || document.hidden) return;
        const delay = useRemaining ? this.remainingInterval : this.interval;
        this.nextAutoplayAt = performance.now() + delay;
        this.autoplayTimer = window.setTimeout(() => {
          this.nextAutoplayAt = 0;
          this.remainingInterval = this.interval;
          this.goTo(this.physicalIndex() + 1);
          this.scheduleAutoplay(false);
        }, delay);
      }

      onScroll() {
        clearFrame(this, this.raf);
        this.raf = queueFrame(this, () => this.updateActive());
        clearTimeout(this.normalizeTimer);
        this.pauseAutoplay();
        this.normalizeTimer = window.setTimeout(() => {
          this.normalizeLoop();
          this.scheduleAutoplay();
        }, 360);
      }

      onPointerDown(event) {
        if (event.pointerType !== 'touch' && event.button !== 0) return;
        if (event.isPrimary === false) return;
        this.pauseAutoplay();
        clearFrame(this, this.scrollAnimationFrame);
        clearFrame(this, this.modeResetFrame);
        this.viewport.classList.remove('is-animating');
        this.announceNext = true;
        if (event.target.closest('a, button, input, textarea, select')) return;
        this.pointerHeld = true;
        this.pointerType = event.pointerType;
        // Touch scrolling stays browser-native so Safari/Chrome retain their momentum.
        // Only the desktop pointer path needs a custom drag implementation.
        this.trackingPointer = event.pointerType !== 'touch';
        this.activePointerId = event.pointerId;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.startScroll = this.viewport.scrollLeft;
        this.lastX = event.clientX;
        this.lastTime = performance.now();
        this.velocity = 0;
        this.pointerMoved = false;
        // Capture from mouse/pen down, before the drag threshold. Otherwise a
        // release outside the viewport is missed and the next hover can drag.
        if (this.trackingPointer) this.viewport.setPointerCapture(event.pointerId);
      }

      onPointerMove(event) {
        if (!this.trackingPointer) return;
        if (event.pointerId !== this.activePointerId) return;
        if ((event.buttons & 1) === 0) {
          this.trackingPointer = false;
          this.activePointerId = null;
          this.pointerHeld = false;
          this.dragging = false;
          this.viewport.classList.remove('is-dragging');
          if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
          this.scheduleAutoplay();
          return;
        }
        if (!this.dragging) {
          const deltaX = event.clientX - this.startX;
          const deltaY = event.clientY - this.startY;
          if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
          this.dragging = true;
          this.pointerMoved = true;
          this.viewport.classList.add('is-dragging');
          this.viewport.setPointerCapture(event.pointerId);
        }
        if (!this.dragging) return;
        event.preventDefault();
        const now = performance.now();
        const velocity = (this.lastX - event.clientX) / Math.max(1, now - this.lastTime);
        this.velocity = this.velocity * 0.25 + velocity * 0.75;
        this.lastX = event.clientX;
        this.lastTime = now;
        this.viewport.scrollLeft = this.startScroll - (event.clientX - this.startX);
      }

      onPointerUp(event) {
        if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;
        this.trackingPointer = false;
        this.activePointerId = null;
        this.pointerHeld = false;
        if (this.dragging) {
          this.dragging = false;
          this.viewport.classList.remove('is-dragging');
          if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
          this.suppressClickUntil = performance.now() + 250;
          const velocity = performance.now() - this.lastTime < 100 ? this.velocity : 0;
          const momentum = Math.max(-4, Math.min(4, velocity)) * 240;
          const projectedLeft = this.viewport.scrollLeft + momentum;
          this.goTo(this.physicalIndex(projectedLeft));
        }
        clearTimeout(this.normalizeTimer);
        this.normalizeTimer = window.setTimeout(() => {
          this.normalizeLoop();
          this.scheduleAutoplay();
        }, 360);
        this.scheduleAutoplay();
      }

      onClick(event) {
        if (event.target.closest('a, button, input, textarea, select')) return;
        if (performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          return;
        }
        const rect = this.viewport.getBoundingClientRect();
        const contentX = this.viewport.scrollLeft + event.clientX - rect.left;
        let index = 0;
        let distance = Infinity;
        this.slides.forEach((slide, current) => {
          const nextDistance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - contentX);
          if (nextDistance < distance) {
            distance = nextDistance;
            index = current;
          }
        });
        this.announceNext = true;
        this.pauseAutoplay();
        this.goTo(index);
      }

      onKeyDown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.announceNext = true;
        this.goTo(this.physicalIndex() + (event.key === 'ArrowRight' ? 1 : -1));
      }

      onResize() {
        const width = this.viewport.clientWidth;
        if (width === this.measuredViewportWidth) return;
        this.measuredViewportWidth = width;
        const logical = this.activeLogicalIndex ?? this.logicalIndex(this.physicalIndex());
        clearFrame(this, this.resizeFrame);
        this.resizeFrame = queueFrame(this, () => {
          this.jumpTo(this.slideCount > 1 ? this.slideCount + logical : logical);
          this.updateActive();
        });
      }

      onMouseEnter(event) {
        if (event?.pointerType === 'touch') return;
        this.hovered = true;
        this.pauseAutoplay();
      }

      onMouseLeave(event) {
        if (event?.pointerType === 'touch') return;
        this.hovered = false;
        this.scheduleAutoplay(true);
      }

      onFocusIn() {
        this.focused = true;
        this.pauseAutoplay();
      }

      onFocusOut(event) {
        if (event?.relatedTarget && this.viewport.contains(event.relatedTarget)) return;
        this.focused = false;
        this.scheduleAutoplay(true);
      }

      onWheel() {
        this.announceNext = true;
        this.pauseAutoplay();
        window.clearTimeout(this.wheelTimer);
        this.wheelTimer = window.setTimeout(() => this.scheduleAutoplay(true), 180);
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.scheduleAutoplay(true);
        }
      }
    }

    customElements.define('dh-ingredient-carousel', DhIngredientCarousel);
  }
})();
